// HAL fetch wrapper — the single code path through which the frontend
// talks to the Go API. Every request in the app goes through halFetch
// (directly or via a TanStack Query hook that calls it).
//
// Responsibilities:
//
//   1. Bearer auth: attaches `Authorization: Bearer <access_token>` from
//      lib/tokens.ts on every request. The API is JSON-only / cookie-free
//      and the SPA owns the token lifecycle.
//
//   2. Transparent refresh: on a 401 response, attempts a one-shot
//      refresh against POST /api/auth/token with grant_type=refresh_token,
//      stores the rotated pair, and retries the original request once.
//      A refresh failure clears local tokens and lets the error bubble;
//      the _app route guard maps it to a /login redirect. After a
//      successful refresh the retry runs without a second refresh, so a
//      401 on the retry bubbles with the tokens left in place. Concurrent
//      401s share one refresh, so a single-use rotating refresh token is
//      spent once (TMF-490). A refresh request that gets no answer
//      (aborted by a navigation, offline) throws and leaves the tokens
//      alone.
//
//   3. JSON request bodies are auto-serialized when `body` is a plain
//      object; Content-Type: application/json is set automatically. If
//      the caller passes a string or FormData, it's forwarded unchanged.
//
//   4. Error responses (non-2xx) throw a typed HalError carrying the
//      status, the parsed error body (if any), and the URL.
//
//   5. Success responses are parsed as JSON and cast to the caller's
//      expected type. HalResource<T> helps callers type both the
//      resource fields and the _links/_embedded envelope.
//
// Not responsibilities:
//   - Retries beyond the single refresh-and-retry (TanStack Query owns
//     transport retries via queryClient config).
//   - Caching, deduplication, optimistic updates (TanStack Query).

import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setTokens,
} from "~/lib/tokens";

// ── Types ─────────────────────────────────────────────────────────────

/**
 * HAL link shape — a single href plus optional metadata. The backend
 * emits these under `_links` in every resource response.
 */
export interface HalLink {
  href: string;
  templated?: boolean;
  type?: string;
  title?: string;
}

/**
 * Envelope shape every HAL resource from the Go API carries. T is the
 * resource's own fields; _links and _embedded are the HAL extras.
 *
 * Typical usage:
 *
 *   type Workflow = HalResource<{
 *     id: string;
 *     name: string;
 *     definition: WorkflowDefinition;
 *   }>;
 *
 *   const wf = await halFetch<Workflow>(`/api/workflows/${id}`);
 *   wf.name               // typed
 *   wf._links.self.href   // typed
 */
export type HalResource<T = Record<string, unknown>> = T & {
  _links: Record<string, HalLink>;
  _embedded?: Record<string, unknown>;
};

/** goat query.Pagination. Every field is omitempty on the Go side, so all of
 *  them vanish at zero; `size` is the page-size field. */
export interface Pagination {
  page?: number;
  size?: number;
  total?: number;
  pages?: number;
}

/** Envelope every paginated HAL collection from the Go API carries — goat
 *  hal.Collection: Pagination + embeds + links. `_embedded` is omitted on an
 *  empty page (hal.ResourceEmbeds is omitempty), so read rows as
 *  `col._embedded ?? []`. */
export type HalCollection<T> = Pagination & {
  _embedded?: T[];
  _links: Record<string, HalLink>;
};

/**
 * HTTP methods halFetch knows about. Restricted to the set the Go API
 * serves — extend only if the API grows to use OPTIONS or HEAD as
 * routable verbs (they're not today).
 */
export type HalMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/**
 * Options for halFetch. All fields optional. Omit `method` for GET.
 */
export interface HalFetchOptions {
  method?: HalMethod;
  /**
   * Request body. Plain objects are JSON-stringified. Strings and
   * FormData pass through unchanged. Null/undefined means no body.
   */
  body?: unknown;
  /**
   * Extra headers to merge with the defaults. Caller headers override
   * defaults on conflict.
   */
  headers?: Record<string, string>;
  /**
   * Abort signal — TanStack Query passes one automatically when a
   * query is cancelled.
   */
  signal?: AbortSignal;
}

/**
 * Thrown on non-2xx responses. Carries enough info for error boundaries
 * and toast displays to decide what to do.
 */
export class HalError extends Error {
  readonly status: number;
  readonly url: string;
  readonly body: unknown;

  constructor(status: number, url: string, body: unknown, message?: string) {
    super(message ?? `HAL request failed: ${status} ${url}`);
    this.name = "HalError";
    this.status = status;
    this.url = url;
    this.body = body;
  }

  /**
   * Convenience check for 401 so route guards and query error handlers
   * can branch on "user is not authenticated" without parsing the body.
   */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /**
   * 403 — authenticated but not permitted. Distinct from 401 (not
   * authenticated). Catching code usually maps this to a "you don't
   * have access" toast rather than a login redirect.
   */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

// ── Refresh-token grant ───────────────────────────────────────────────

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Trade the stored refresh token for a fresh access+refresh pair via
 * POST /api/auth/token. Uses raw fetch (not halFetch) to avoid both
 * the bearer-attach and the recursive refresh-on-401 paths.
 *
 * Returns true on success; the new pair is in lib/tokens.ts. Returns
 * false when the server answered and refused the grant (no refresh
 * token, non-2xx, malformed body) — the caller treats false as "the
 * user is logged out." Throws when the request itself got no answer
 * (aborted by a navigation, offline): that is not a verdict on the
 * session, so callers must not clear tokens for it. Callers that
 * arrive while a refresh is in flight join it and see its result.
 */
async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight !== null) return refreshInFlight;

  refreshInFlight = (async () => {
    const refresh = getRefreshToken();
    if (refresh === null) return false;

    const body = new URLSearchParams();
    body.set("grant_type", "refresh_token");
    body.set("refresh_token", refresh);

    const resp = await fetch(buildUrl("/api/auth/token"), {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
    if (!resp.ok) return false;
    let data: { access_token?: string; refresh_token?: string };
    try {
      data = (await resp.json()) as {
        access_token?: string;
        refresh_token?: string;
      };
    } catch {
      return false;
    }
    if (
      typeof data.access_token !== "string" ||
      typeof data.refresh_token !== "string"
    ) {
      return false;
    }
    setTokens(data.access_token, data.refresh_token);
    return true;
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

// ── API base URL ──────────────────────────────────────────────────────

// When running locally, VITE_API_BASE_URL is unset so this falls back
// to an empty string, which means relative URLs like `/api/auth/me`
// pass through to the current origin and get proxied to the Go API by
// Vite's dev server. In staging and production, the deploy workflow
// fetches the API domain from the app stack's CloudFormation outputs
// and exports VITE_API_BASE_URL before `vite build`, so the full API
// origin (e.g., `https://mayfirm-api.staging.bitstormtech.io`) gets
// inlined into the bundle and prepended to every relative URL. This
// keeps environment config out of the repo — no committed per-env
// files. Absolute URLs (already starting with http:// or https://)
// are passed through unchanged.
const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "";

export function buildUrl(url: string): string {
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${API_BASE_URL}${url}`;
}

/**
 * Fetch a HAL resource from the Go API.
 *
 * @param url    The API URL — absolute or relative. Relative URLs pass
 *               through to the current origin, which is the intended
 *               path for the SPA (Vite proxies /api to the Go API
 *               during dev; in staging/prod the frontend and API share
 *               a registrable domain so relative URLs work).
 * @param opts   Request options. Omit for a simple GET.
 * @returns      The parsed resource typed as T. Callers supply a type
 *               parameter that extends HalResource.
 * @throws       HalError on non-2xx responses. Network failures throw
 *               the underlying TypeError from fetch().
 */
export async function halFetch<T>(
  url: string,
  opts: HalFetchOptions = {},
): Promise<T> {
  return halFetchOnce<T>(url, opts, true);
}

// halFetchOnce is the inner implementation. `allowRefresh` controls
// whether a 401 should trigger the refresh-and-retry path. The public
// entry sets it to true; the retried call after a successful refresh
// sets it to false to avoid an infinite loop if the new access token
// is also somehow rejected.
async function halFetchOnce<T>(
  url: string,
  opts: HalFetchOptions,
  allowRefresh: boolean,
): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = {
    Accept: "application/json, application/hal+json",
    ...opts.headers,
  };

  const accessToken = getAccessToken();
  if (accessToken !== null && headers["Authorization"] === undefined) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  // Body handling: plain objects → JSON, strings/FormData → pass
  // through unchanged. Undefined/null means no body.
  let body: BodyInit | undefined;
  if (opts.body != null) {
    if (
      typeof opts.body === "string" ||
      opts.body instanceof FormData ||
      opts.body instanceof URLSearchParams ||
      opts.body instanceof Blob ||
      opts.body instanceof ArrayBuffer
    ) {
      body = opts.body as BodyInit;
    } else {
      body = JSON.stringify(opts.body);
      if (headers["Content-Type"] === undefined) {
        headers["Content-Type"] = "application/json";
      }
    }
  }

  // Build the RequestInit conditionally — exactOptionalPropertyTypes
  // treats `undefined` and "missing" as distinct, so we can't just
  // pass `body: undefined` or `signal: undefined` through.
  const init: RequestInit = {
    method,
    headers,
  };
  if (body !== undefined) {
    init.body = body;
  }
  if (opts.signal !== undefined) {
    init.signal = opts.signal;
  }
  const response = await fetch(buildUrl(url), init);

  // 401: try the refresh grant once, then retry the original call. If
  // refresh fails (or the retried call also 401s), clear local tokens
  // so the next route-guard pass redirects to /login.
  if (response.status === 401 && allowRefresh) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      return halFetchOnce<T>(url, opts, false);
    }
    clearTokens();
  }

  // Parse body once. Empty bodies are fine — 204 responses in
  // particular return no content.
  const contentType = response.headers.get("Content-Type") ?? "";
  const isJson = contentType.includes("json");
  let parsedBody: unknown = null;
  if (response.status !== 204) {
    const text = await response.text();
    if (text.length > 0) {
      if (isJson) {
        try {
          parsedBody = JSON.parse(text);
        } catch {
          parsedBody = text; // fall back to raw on malformed json
        }
      } else {
        parsedBody = text;
      }
    }
  }

  if (!response.ok) {
    throw new HalError(response.status, url, parsedBody);
  }

  return parsedBody as T;
}
